import type { ImportSummary } from "@nu-assist/contracts";
import { ProgressBar } from "@nu-assist/ui";
import { ArrowDown, ArrowUp, ArrowUpDown, Download, Sparkles, TreeDeciduous } from "lucide-react";
import type { SortState } from "../../../lib/sort";
/**
 * ImportsTable — the firm-wide imports table (R-F4..R-F12).
 *
 * Columns: Date | Filename | Search | Contacts | Imported by | Tags | Actions.
 * The Search cell renders the shared `ImportSearchLinks` — Thrive id out to
 * Thrive, search name in to our Search Detail page — the same pairing the import
 * picker uses, so the two screens do not disagree about one concept.
 * Own rows render the "Imported by" cell accent+bold (R-F5). Counts use
 * `toLocaleString()` (R-F8). Conflicts pill renders when `conflict_count > 0`
 * (R-F7). Row click → Import Detail (R-F10); action controls `stopPropagation`
 * so they don't trigger row navigation (R-F11). Empty state per R-F12.
 *
 * Action-cell affordances are surfaced from the row's `can_*` flags: Download,
 * "Feed into Company Map", and "AI Review".
 *
 * "Feed into BCT" and "AI Review" were hidden on 2026-08-05 (Jose feedback,
 * row-level decluttering) and RESTORED on 2026-08-10 with the release of the
 * BCT and AI Review pages themselves — with both destinations released, the
 * row shortcut is a shortcut to something a recruiter can now actually reach.
 * The restoration cost one import and two JSX blocks because the hiding was
 * done as a hide: `can_feed_bct`/`can_feed_ai` were deliberately left in the
 * wire contract rather than deleted, and the comment at the time said so.
 * Worth copying the next time UI is hidden that might come back.
 *
 * "Compare to Thrive" is NOT restored, and that asymmetry is the point: it was
 * DELETED on 2026-08-05 rather than hidden, because it was always an inert
 * button whose target flow is a separate, never-built EPIC. There is no
 * feature behind it to restore.
 */
import { useLocation, useNavigate } from "react-router-dom";
import { displayTitle } from "../import-detail/importTitle";

import { ImportSearchLinks } from "../../../features/imports/selection/ImportSearchLinks";
import { ConflictPill } from "./ConflictPill";
import { ContactsStateBar } from "./ContactsStateBar";
import { TagPills } from "./TagPills";

export interface ImportsTableProps {
  items: readonly ImportSummary[];
  meEmail: string;
  /** Download the import's original file (presigned GET). Wired by the page. */
  onDownload?: (importId: string, filename: string) => void;
  sort?: SortState;
  onSort?: (key: string) => void;
}

type ColDef = { label: string; sortKey?: string };

const COLUMNS: ColDef[] = [
  { label: "Date", sortKey: "created_at" },
  { label: "People list title", sortKey: "filename" },
  { label: "Search" },
  { label: "Contacts", sortKey: "total_contacts" },
  { label: "Imported by", sortKey: "uploader_email" },
  { label: "Type", sortKey: "import_type" },
  { label: "Actions" },
];

/** The Date column's format. Exported for Company Lists' table, which shows
 *  its Date column the same way (Trello #831). */
export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isOwnRow(ownerEmail: string, meEmail: string): boolean {
  return ownerEmail.trim().toLowerCase() === meEmail.trim().toLowerCase();
}

// "Own" tracks the same notion the "You" owner scope filters by: a row is
// mine when I imported the raw file OR when I created this instance (BCT
// trigger). Accent-only widening — the "Imported by" cell keeps naming the
// uploader. Without the created_by half, a BCT child the viewer triggered
// would appear in their default "You" scope rendered as somebody else's row.
function isOwnByEither(
  uploaderEmail: string,
  createdBy: string | null | undefined,
  meEmail: string
): boolean {
  return isOwnRow(uploaderEmail, meEmail) || (createdBy != null && isOwnRow(createdBy, meEmail));
}

export function ImportsTable({ items, meEmail, onDownload, sort, onSort }: ImportsTableProps) {
  const navigate = useNavigate();
  // Where the reader is standing, handed to the detail page so its "Back to
  // People Lists" link returns to the LIST THEY WERE READING rather than to a
  // bare `/research/imports`. Browser Back already works — the filters live in
  // the query string — but the in-app link is a push to a path, so without this
  // it would drop the filters the reader had just set.
  //
  // The whole `pathname + search`, opaque: it carries the scope, the chips, the
  // search box, the page, `search_id_thrive` and `?preview=` in one string,
  // with no second serializer to drift out of step with the first.
  const location = useLocation();

  if (items.length === 0) {
    return (
      <div
        data-testid="imports-empty"
        className="rounded-md border border-border bg-surface px-4 py-12 text-center text-body text-text-secondary"
      >
        No people lists match the current filter / search.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-table-surface">
      <table className="w-full border-collapse" data-testid="imports-table">
        <thead>
          <tr className="border-b border-border text-left">
            {COLUMNS.map((col) => {
              const active = col.sortKey && sort?.key === col.sortKey;
              const sortable = !!col.sortKey && !!onSort;
              const dir = sort?.dir ?? "asc";
              const handleSort = sortable ? () => onSort(col.sortKey as string) : undefined;
              return (
                <th
                  key={col.label}
                  scope="col"
                  className="text-label font-semibold text-text-secondary"
                  aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : sortable ? "none" : undefined}
                >
                  {sortable ? (
                    <button
                      type="button"
                      className={`inline-flex w-full items-center gap-1 px-3 py-2 text-left cursor-pointer select-none hover:text-text${active ? " text-accent" : ""}`}
                      onClick={handleSort}
                    >
                      {col.label}
                      {active ? (
                        dir === "asc"
                          ? <ArrowUp aria-hidden="true" className="h-3 w-3 text-accent" />
                          : <ArrowDown aria-hidden="true" className="h-3 w-3 text-accent" />
                      ) : (
                        <ArrowUpDown aria-hidden="true" className="h-3 w-3 text-text-muted" />
                      )}
                    </button>
                  ) : (
                    <span className="block px-3 py-2">{col.label}</span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const own = isOwnByEither(row.uploader_email, row.created_by, meEmail);
            const ownerLabel = row.owner_name ?? row.uploader_email;
            const open = () =>
              navigate(`/research/imports/${row.import_id}`, {
                state: { from: `${location.pathname}${location.search}` },
              });
            return (
              <tr
                key={row.import_id}
                data-testid="imports-row"
                data-own={own ? "true" : "false"}
                // The row STOPPED SHOWING lifecycle state when the Tags column
                // became Type (Trello #711 cl.1) — it did not stop knowing it.
                // Two e2e specs used the `exported` pill as their only way to
                // tell compare.spec's row from export.spec's (same fixture
                // filename, same Postgres), and one of them asserted a dry-run
                // compare does not stamp the tag. Dropping the pill without
                // this would leave both silently unfalsifiable rather than red.
                //
                // `-state` because `data-exported` is already taken, one
                // directory over, as a COUNT (`CompareProgressDivider.tsx:60`,
                // pinned as "42" by its test). Two meanings for one attribute
                // name inside the same feature area is a trap for whoever
                // greps next.
                data-exported-state={(row.tags ?? []).includes("exported") ? "true" : "false"}
                data-import-id={row.import_id}
                tabIndex={0}
                // The STORED name, not the displayed stem. The visible text is
                // stripped for looks, but the accessible name is how a screen
                // reader tells two lists apart — and `acme.csv` / `acme.json`
                // are indistinguishable once stripped (`/pr-review` iter 2,
                // Agent C I4b).
                aria-label={`Open people list ${row.filename}`}
                onClick={open}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    open();
                  }
                }}
                className="cursor-pointer border-b border-border last:border-b-0 hover:bg-row-hover focus:outline-none focus:bg-row-hover"
              >
                <td className="whitespace-nowrap px-3 py-2 text-body text-text-secondary">
                  {formatDate(row.created_at)}
                </td>
                <td className="px-3 py-2 text-body text-text">
                  <span className="flex flex-wrap items-center gap-2">
                    {/* `title` carries the STORED name. Two lists whose names differ only
                        by extension render identically once the suffix is hidden
                        (`acme.csv` / `acme.json`), so the full value stays one hover
                        away rather than being unrecoverable (`/pr-review` iter 1,
                        Agent C I4). */}
                    <span className={own ? "font-semibold" : ""} title={row.filename}>
                      {displayTitle(row.filename)}
                    </span>
                    <ConflictPill count={row.conflict_count} />
                  </span>
                </td>
                <td className="px-3 py-2 text-body text-text-secondary">
                  {/* Retrofitted onto the shared `ImportSearchLinks` so the
                      listing and the import PICKER answer "what happens when I
                      click a search name?" the same way. This cell used to render
                      ONE anchor to Thrive labelled `{id} - {name}`, with no route
                      to our own Search Detail page — so once the picker gained
                      the id/name pair, the two screens disagreed about the same
                      concept. Fixing only the picker would have replaced one
                      inconsistency with another. */}
                  <ImportSearchLinks
                    searchIdThrive={row.search_id_thrive ?? null}
                    searchName={row.search_name ?? null}
                    testIdPrefix={`imports-row-search-${row.import_id}`}
                    // In-tab: nothing unsaved on this screen, and `SearchesTable`
                    // already navigates in-tab to the same page.
                    newTab={false}
                    // A long search name wraps inside a capped column, never
                    // cut (Jose, 2026-10-08): uncapped, its one long line pushed
                    // the Actions column off a 1280 px screen. `max-w-56`
                    // (224 px) puts a typical title on two or three lines.
                    wrapSearchName
                    className="max-w-56"
                  />
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  <ContactsStateBar row={row} />
                </td>
                <td
                  data-testid="imported-by-cell"
                  className={
                    own
                      ? "px-3 py-2 text-body text-accent font-semibold"
                      : "px-3 py-2 text-body text-text-secondary"
                  }
                >
                  {ownerLabel}
                </td>
                <td className="px-3 py-2">
                  <span className="flex flex-wrap items-center gap-2">
                    {/* The provenance pill names whoever PRODUCED the file.
                        That is `created_by` (#676): the BCT run's triggerer, the
                        AI review's reviewer. `uploader_email` is the raw
                        ancestor's importer and is only the fallback for rows
                        written before 0031's split.

                        One prop, not two. #646 shipped `bctRunnerEmail` and
                        #669 `reviewerName`, doing one thing — two props for one
                        concept is how the `bct` and `ai` pills would have
                        drifted into naming their producer differently.

                        Gated on `import_format`, the immutable provenance
                        discriminator — the same key the server's D5 no-chaining
                        409 and `aiReviewUnusable` key on. NOT `source`, which is
                        the CHANNEL a file arrived through and is orthogonal.

                        Without the gate a legacy in-place review (D6) — a raw
                        import that carries the `ai` tag, whose creator is the
                        IMPORTER — would be labelled as if that person had
                        reviewed it. */}
                    <TagPills
                      tags={row.tags ?? []}
                      importFormat={row.import_format}
                      producedBy={
                        row.import_format === "ai_review" || row.import_format === "bct"
                          ? (row.created_by ?? row.uploader_email ?? null)
                          : null
                      }
                    />
                    {row.export_in_progress ? (
                      <span
                        data-testid="export-in-progress-pill"
                        className="inline-flex items-center gap-1 rounded-pill bg-accent-muted px-2 py-0.5 text-caption text-accent"
                      >
                        {/* Fixed width: the shared bar fills its parent, and
                            a pill has none to give. */}
                        <div className="w-6 shrink-0">
                          <ProgressBar indeterminate live label="Exporting" />
                        </div>
                        Exporting…
                      </span>
                    ) : null}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <RowActions row={row} onDownload={onDownload} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * RowActions — the affordance icons the row's `can_*` flags permit. Each button
 * stops propagation so it doesn't trigger the row's navigation.
 */
function RowActions({
  row,
  onDownload,
}: {
  row: ImportSummary;
  onDownload?: (importId: string, filename: string) => void;
}) {
  const navigate = useNavigate();
  // stopPropagation lives on each button (not a wrapper) so an action click
  // never bubbles to the row's navigate handler (R-F11). Pure click guard — no
  // keyboard pairing needed (these are real <button>s, keyboard-activable).
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const btn =
    "inline-flex items-center justify-center rounded-md p-1.5 text-text-secondary hover:bg-accent-muted hover:text-accent";
  // `can_download === false` covers TWO populations, and telling a recruiter
  // the wrong one is worse than saying nothing. A row that is `uploaded` /
  // `parsing` will become downloadable. A `failed` one never will — and
  // because the API only clears the flag on `failed` for worker-written
  // formats (`ai_review` / `bct`), a `failed` row that reaches this branch is
  // always a derived file whose builder died. That is the terminal state the
  // sweeper produces, i.e. the COMMONEST failure of AI Review, and the row's
  // own contacts cell already says "Failed" in error colour two columns over.
  // A "Still processing…" tooltip next to it is a straight contradiction, and
  // it reintroduces the "no way to tell 'not yet' from 'never'" that the
  // disabled-not-hidden decision below exists to avoid.
  const downloadTitle = row.can_download
    ? "Download"
    : row.status === "failed"
      ? "This file was never created — the run that would have built it failed"
      : "Still processing — the file is not ready to download yet";
  return (
    <span className="inline-flex items-center gap-1" data-testid="row-actions">
      {/* DISABLED, never hidden, and that is this product's idiom for a row you
          cannot act on yet rather than a style preference. `can_download` used
          to be true for every row, so the `null` arm was unreachable; it became
          reachable when the AI Review derived file started existing BEFORE its
          object does. A button that silently appears part-way through a poll
          gives the recruiter no way to tell "not yet" from "never", while the
          rest of this screen says "Still processing" out loud. */}
      <button
        type="button"
        className={row.can_download ? btn : `${btn} cursor-not-allowed opacity-40`}
        aria-label="Download"
        title={downloadTitle}
        data-testid="row-download"
        // `aria-disabled`, not `disabled`: a `disabled` button drops out of tab
        // order, so a keyboard or screen-reader user can never reach the very
        // `title` that carries the reason. Same pattern the ImportPicker's
        // unusable rows already use. The click guard moves into the handler.
        aria-disabled={!row.can_download}
        aria-describedby={row.can_download ? undefined : `${row.import_id}-dl-why`}
        onClick={(e) => {
          stop(e);
          if (!row.can_download) return;
          onDownload?.(row.import_id, row.filename);
        }}
      >
        <Download className="h-4 w-4" aria-hidden="true" />
      </button>
      {row.can_download ? null : (
        <span id={`${row.import_id}-dl-why`} className="sr-only">
          {downloadTitle}
        </span>
      )}
      {/* Enrich row action removed 2026-07-16 (per-user direction): enrichment
          is now an INTERMEDIATE step inside Export (subtask 03/04's
          export_worker enrichment phase + Import Detail's "Enrich Data"
          column), not a standalone user-triggered action — `can_enrich` is
          intentionally left unused here rather than removed from the wire
          contract, since the backend flag may still gate something else. */}
      {/* HIDDEN when unavailable, unlike Download two elements up — and the
          asymmetry is the decision, not an oversight.
          
          An earlier revision of this file unhid it "for consistency" and got
          the tooltip wrong in the process, because `can_feed_bct` is false for
          TWO populations and only one of them is temporary: `"bct" not in
          tagset AND not has_no_object_yet(...)` (`imports/domain.py:390`). A
          Company Map output can never feed another tree, so it would have worn
          a permanently greyed-out button reading "Still processing" — and
          every row ever fed would have carried that clutter forever, undoing
          the 2026-08-05 decluttering recorded at the top of this file.

          Download is genuinely different, which is why it stays disabled-not-
          hidden: it is the artifact the recruiter was PROMISED and is actively
          waiting for, so its absence reads as a lost file. These two are
          optional onward actions; a row that cannot feed Company Map is not a
          broken promise, and a shortcut appearing once the file is ready is
          ordinary progressive enablement. */}
      {row.can_feed_bct ? (
        <button
          type="button"
          className={btn}
          aria-label="Feed into Company Map"
          title="Feed into Company Map"
          data-testid="row-feed-bct"
          onClick={(e) => {
            stop(e);
            // No `?preview=1` on this link any more. The pre-2026-08-05
            // version carried one, which never worked: `bct` is a Phase 2 id,
            // so `?preview=1` was the one preview mode that did NOT reveal it
            // (phases are cumulative upward, not downward). The page is
            // released now, so the deep link needs no preview at all.
            navigate(`/research/company-map?importId=${row.import_id}`);
          }}
        >
          <TreeDeciduous className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
      {row.can_feed_ai ? (
        <button
          type="button"
          className={btn}
          aria-label="AI Review"
          title="AI Review"
          data-testid="row-feed-ai-review"
          onClick={(e) => {
            stop(e);
            navigate(`/research/ai-review?import=${row.import_id}`);
          }}
        >
          <Sparkles className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
      {/* "Compare to Thrive" row action DELETED 2026-08-05 (Jose feedback) —
          unlike the two above, this was never wired to anything (its target
          flow was a separate, never-built EPIC — see the removed
          `can_compare && import_format !== "bct"` inert button), so there's
          no feature to preserve reachability to. */}
    </span>
  );
}
